import { ImageRatio } from '@diritto/common/base/constants';
import { Color } from '@diritto/common/style/color';
import { Font } from 'constants/contentCover';
import { useCallback, useEffect, useState } from 'react';
import { convertImageDataURLToFile } from 'utils/image';

export const useImageEditor = (targetElement: HTMLElement | null, imageUrl: string) => {
  const [imageEditor, setImageEditor] = useState<tuiImageEditor.ImageEditor>();
  const [activeObj, setActiveObj] = useState<tuiImageEditor.ITextObjectProps>();

  useEffect(() => {
    (async () => {
      if (targetElement && imageUrl) {
        const ImageEditor = (await import('tui-image-editor')).default;

        setImageEditor(
          new ImageEditor(targetElement, {
            includeUI: {
              loadImage: {
                path: imageUrl,
                name: 'Cover Image',
              },
            },
            cssMaxWidth: targetElement.clientWidth,
            cssMaxHeight: targetElement.clientWidth * ImageRatio,
            usageStatistics: false,
            selectionStyle: {
              cornerStyle: 'circle',
              cornerSize: 18,
              cornerColor: Color.Gray400,
              cornerStrokeColor: Color.Gray400,
              transparentCorners: false,
              lineWidth: 2,
              borderColor: Color.Gray400,
            },
          }),
        );
      }
    })();
  }, [imageUrl, targetElement]);

  useEffect(() => {
    imageEditor?.on('objectActivated', props => {
      // https://nhn.github.io/tui.image-editor/latest/ImageEditor#startDrawingMode
      // 텍스트 모드가 아니면 텍스트 모드로 변경. tui에 별도의 props의 type이 정의 되어 있지 않다.
      const isText = ['i-text', 'text'].includes(props.type);
      if (isText) {
        if (imageEditor.getDrawingMode() !== 'TEXT') {
          imageEditor.stopDrawingMode();
          imageEditor.startDrawingMode('TEXT');
        }
      }
      setActiveObj(props);
    });

    imageEditor?.on('selectionCleared', () => {
      setActiveObj(undefined);
    });
  }, [imageEditor]);

  const addText = useCallback(
    async (text: string, textColor?: string, fontFamily?: Font) => {
      if (!imageEditor) {
        return;
      }
      try {
        const res = await imageEditor.addText(text, {
          styles: {
            fontSize: 100,
            fontFamily,
            fill: textColor,
          },
          position: {
            x: 10,
            y: 200,
          },
        });
        setActiveObj(res);
      } catch (e) {
        // Nothing
      } finally {
        imageEditor.stopDrawingMode();
      }
    },
    [imageEditor],
  );

  const changeTextColor = useCallback(
    async (id: number, textColor: string) => {
      if (!imageEditor) {
        return;
      }

      try {
        await imageEditor.changeTextStyle(id, {
          fill: textColor,
        });
        setActiveObj(prev => ({ ...prev, fill: textColor }) as tuiImageEditor.ITextObjectProps);
      } catch {
        // Nothing
      }
    },
    [imageEditor],
  );

  const changeTextFont = useCallback(
    async (id: number, fontFamily: Font) => {
      if (!imageEditor) {
        return;
      }

      try {
        await imageEditor.changeTextStyle(id, {
          fontFamily,
        });
        setActiveObj(prev => ({ ...prev, fontFamily }) as tuiImageEditor.ITextObjectProps);
      } catch {
        // Nothing
      }
    },
    [imageEditor],
  );

  const clearObjects = useCallback(async () => {
    if (!imageEditor) {
      return;
    }

    try {
      await imageEditor.clearObjects();
    } catch {
      // Nothing
    }
  }, [imageEditor]);

  const getImageFile = useCallback(async () => {
    const imageData = imageEditor?.toDataURL({ format: 'jpeg' });
    if (!imageData) {
      throw new Error('imageEditor?.toDataURL is empty');
    }

    return convertImageDataURLToFile(imageData);
  }, [imageEditor]);

  return {
    isLoaded: !!imageEditor,
    activeObj,
    clearObjects,
    addText,
    changeTextColor,
    changeTextFont,
    getImageFile,
  };
};
